/**
 * 🏆 Excelência Governance Evolution Service
 * 
 * Implementa funcionalidades avançadas de governança:
 * - Indicador de execuções ativas por versão
 * - Bloqueio de edição quando há execuções ativas
 * - Comparador de versões (diff snapshot_json)
 * - Permissões granulares por role
 */
import { supabase } from '../../../lib/supabaseClient';
import { canPerformGovernanceAction, type GovernanceAction, type GovernanceRole } from './governancePermissions';
import { compareVersions, type VersionDiff } from './version.service';

// ============================================================================
// EXECUÇÕES ATIVAS POR VERSÃO
// ============================================================================

export interface VersionExecutionCount {
  versionId: string;
  versionSemver: string;
  activeCount: number;
  completedCount: number;
  totalCount: number;
}

/**
 * Conta execuções ativas por versão de um blueprint
 */
export async function getActiveExecutionsByVersion(
  blueprintId: string,
  organizationId: string,
): Promise<VersionExecutionCount[]> {
  if (!blueprintId || !organizationId) return [];

  // Buscar versões do blueprint
  const { data: versions, error: versionsError } = await supabase
    .from('process_blueprint_versions')
    .select('id, version_semver')
    .eq('blueprint_id', blueprintId)
    .eq('organization_id', organizationId)
    .order('version', { ascending: false });

  if (versionsError || !versions) return [];

  // Buscar instâncias que referenciam este blueprint
  const { data: instances, error: instancesError } = await supabase
    .from('workflow_instances_v9')
    .select('id, status, blueprint_version_id')
    .eq('organization_id', organizationId)
    .eq('blueprint_id', blueprintId);

  if (instancesError || !instances) {
    return versions.map((v: any) => ({
      versionId: v.id,
      versionSemver: v.version_semver ?? '?',
      activeCount: 0,
      completedCount: 0,
      totalCount: 0,
    }));
  }

  // Agrupar por versão
  const countMap = new Map<string, VersionExecutionCount>();
  versions.forEach((v: any) => {
    countMap.set(v.id, {
      versionId: v.id,
      versionSemver: v.version_semver ?? '?',
      activeCount: 0,
      completedCount: 0,
      totalCount: 0,
    });
  });

  instances.forEach((inst: any) => {
    const versionId = inst.blueprint_version_id;
    if (versionId && countMap.has(versionId)) {
      const entry = countMap.get(versionId)!;
      entry.totalCount += 1;
      if (inst.status === 'active' || inst.status === 'on_hold') {
        entry.activeCount += 1;
      } else if (inst.status === 'completed') {
        entry.completedCount += 1;
      }
    }
  });

  return Array.from(countMap.values());
}

// ============================================================================
// BLOQUEIO DE EDIÇÃO
// ============================================================================

export interface EditLockStatus {
  locked: boolean;
  reason?: string;
  activeExecutionCount: number;
}

/**
 * Verifica se um blueprint pode ser editado
 * Bloqueia edição se houver execuções ativas
 */
export async function checkBlueprintEditLock(
  blueprintId: string,
  organizationId: string,
): Promise<EditLockStatus> {
  if (!blueprintId || !organizationId) {
    return { locked: false, activeExecutionCount: 0 };
  }

  const { count, error } = await supabase
    .from('workflow_instances_v9')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .eq('blueprint_id', blueprintId)
    .in('status', ['active', 'on_hold']);

  if (error) {
    console.warn('[governanceEvolution] Falha ao verificar lock:', error.message);
    return { locked: false, activeExecutionCount: 0 };
  }

  const activeCount = count ?? 0;

  if (activeCount > 0) {
    return {
      locked: true,
      reason: `Existem ${activeCount} execuções ativas deste blueprint. Edição bloqueada para garantir integridade.`,
      activeExecutionCount: activeCount,
    };
  }

  return { locked: false, activeExecutionCount: 0 };
}

// ============================================================================
// COMPARADOR DE VERSÕES (DIFF SNAPSHOT)
// ============================================================================

export interface VersionCompareResult {
  leftVersion: { id: string; semver: string; createdAt: string };
  rightVersion: { id: string; semver: string; createdAt: string };
  diff: VersionDiff<Record<string, unknown>>;
  stagesDiff: {
    added: any[];
    removed: any[];
    modified: Array<{ name: string; changes: Record<string, { from: unknown; to: unknown }> }>;
  };
  summary: {
    totalChanges: number;
    stagesAdded: number;
    stagesRemoved: number;
    stagesModified: number;
  };
}

/**
 * Compara 2 versões de um blueprint via snapshot_json
 */
export async function compareVersionSnapshots(
  leftVersionId: string,
  rightVersionId: string,
  organizationId: string,
): Promise<VersionCompareResult | null> {
  if (!leftVersionId || !rightVersionId || !organizationId) return null;

  const [leftRes, rightRes] = await Promise.all([
    supabase
      .from('process_blueprint_versions')
      .select('id, version_semver, snapshot_json, created_at')
      .eq('id', leftVersionId)
      .eq('organization_id', organizationId)
      .single(),
    supabase
      .from('process_blueprint_versions')
      .select('id, version_semver, snapshot_json, created_at')
      .eq('id', rightVersionId)
      .eq('organization_id', organizationId)
      .single(),
  ]);

  if (leftRes.error || rightRes.error || !leftRes.data || !rightRes.data) {
    return null;
  }

  const leftSnap = (leftRes.data.snapshot_json ?? {}) as Record<string, unknown>;
  const rightSnap = (rightRes.data.snapshot_json ?? {}) as Record<string, unknown>;

  // Diff do snapshot completo
  const diff = compareVersions(leftSnap, rightSnap);

  // Diff de stages especificamente
  const leftStages = (leftSnap.stages ?? []) as any[];
  const rightStages = (rightSnap.stages ?? []) as any[];

  const leftStageMap = new Map(leftStages.map((s: any) => [s.id ?? s.name, s]));
  const rightStageMap = new Map(rightStages.map((s: any) => [s.id ?? s.name, s]));

  const addedStages = rightStages.filter((s: any) => !leftStageMap.has(s.id ?? s.name));
  const removedStages = leftStages.filter((s: any) => !rightStageMap.has(s.id ?? s.name));
  const modifiedStages: VersionCompareResult['stagesDiff']['modified'] = [];

  rightStages.forEach((rightStage: any) => {
    const key = rightStage.id ?? rightStage.name;
    const leftStage = leftStageMap.get(key);
    if (!leftStage) return;

    const changes: Record<string, { from: unknown; to: unknown }> = {};
    Object.keys(rightStage).forEach((field) => {
      if (JSON.stringify(leftStage[field]) !== JSON.stringify(rightStage[field])) {
        changes[field] = { from: leftStage[field], to: rightStage[field] };
      }
    });

    if (Object.keys(changes).length > 0) {
      modifiedStages.push({ name: rightStage.name ?? key, changes });
    }
  });

  const totalChanges =
    Object.keys(diff.added).length +
    Object.keys(diff.removed).length +
    Object.keys(diff.changed).length;

  return {
    leftVersion: {
      id: leftRes.data.id,
      semver: leftRes.data.version_semver ?? '?',
      createdAt: leftRes.data.created_at,
    },
    rightVersion: {
      id: rightRes.data.id,
      semver: rightRes.data.version_semver ?? '?',
      createdAt: rightRes.data.created_at,
    },
    diff,
    stagesDiff: {
      added: addedStages,
      removed: removedStages,
      modified: modifiedStages,
    },
    summary: {
      totalChanges,
      stagesAdded: addedStages.length,
      stagesRemoved: removedStages.length,
      stagesModified: modifiedStages.length,
    },
  };
}

// ============================================================================
// PERMISSÕES GRANULARES POR ROLE
// ============================================================================

export interface GovernancePermissions {
  canCreate: boolean;
  canEdit: boolean;
  canPublish: boolean;
  canArchive: boolean;
  canSetStandard: boolean;
  canDelete: boolean;
  canLink: boolean;
}

/**
 * Retorna mapa de permissões granulares para o role do usuário
 */
export function getGovernancePermissions(role?: GovernanceRole | null): GovernancePermissions {
  return {
    canCreate: canPerformGovernanceAction('create', role),
    canEdit: canPerformGovernanceAction('edit', role),
    canPublish: canPerformGovernanceAction('publish', role),
    canArchive: canPerformGovernanceAction('archive', role),
    canSetStandard: canPerformGovernanceAction('setStandard', role),
    canDelete: canPerformGovernanceAction('delete', role),
    canLink: canPerformGovernanceAction('link', role),
  };
}

/**
 * Verifica permissão com contexto de edit lock
 */
export async function checkGovernancePermission(
  action: GovernanceAction,
  role: GovernanceRole | null | undefined,
  blueprintId?: string,
  organizationId?: string,
): Promise<{ allowed: boolean; reason?: string }> {
  // 1) Verificar permissão do role
  if (!canPerformGovernanceAction(action, role)) {
    return { allowed: false, reason: `Papel "${role ?? 'desconhecido'}" não tem permissão para "${action}"` };
  }

  // 2) Se é edit, verificar lock
  if (action === 'edit' && blueprintId && organizationId) {
    const lock = await checkBlueprintEditLock(blueprintId, organizationId);
    if (lock.locked) {
      return { allowed: false, reason: lock.reason };
    }
  }

  return { allowed: true };
}
